/**
 * App-wide offline HTTP read-cache (desktop only).
 *
 * Wraps `axios.defaults.adapter` ONCE at boot so every axios instance in the
 * app — the shared `apiClient`, the 30+ per-service `axios.create()` clients,
 * and direct `axios.get()` calls — transparently:
 *   - caches successful GET responses into the ENCRYPTED SQLCipher store, and
 *   - serves them back when the app is effectively offline (network down OR the
 *     user toggled "Work Offline"), instead of hanging on a dead request.
 *
 * MUST be imported before any service module creates its axios instance: an
 * instance created BEFORE the override keeps the original adapter (verified).
 * That's why `main.tsx` imports this module first, and it self-installs on
 * import.
 *
 * Gated to Tauri desktop — the web app (and prod money path) is untouched.
 * Reads/writes go through the encrypted store (`cacheEntity`/`getCachedEntity`),
 * so cached PII stays encrypted at rest. Writes are best-effort: any failure
 * (DB not open yet, etc.) falls through without breaking the request.
 */

import axios, {
  AxiosAdapter,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';
import { cacheEntity, getCachedEntity, getForcedOffline } from './desktopOfflineService';
import { isOffline } from './connectivity';
import { UI_LANGUAGE_HEADER, currentUiLanguage } from '../../services/uiLanguageHeader';

const HTTP_CACHE_TYPE = '__http__';
const HTTP_CACHE_TTL_SECONDS = 24 * 60 * 60; // 24h

// Never cache auth/credential traffic.
const NEVER_CACHE = ['/login', '/auth', '/2fa', '/verify', '/token', '/password', '/logout'];

interface CachedHttp {
  status: number;
  data: unknown;
}

function isTauri(): boolean {
  return typeof window !== 'undefined' && '__TAURI__' in window;
}

/**
 * Effective offline = VERIFIED network loss OR user-chosen "Work Offline".
 *
 * This used to read `navigator.onLine`, which false-negatives in the desktop
 * webview: the app would decide it was offline while the network was fine and
 * quietly answer every GET from the cache instead of the API.
 */
function effectiveOffline(): boolean {
  let forced = false;
  try {
    forced = getForcedOffline();
  } catch {
    forced = false;
  }
  return isOffline() || forced;
}

function isGet(config: InternalAxiosRequestConfig): boolean {
  return (config.method || 'get').toLowerCase() === 'get';
}

/**
 * Which organization (and user) a response belongs to is NOT in the URL — it is
 * in the `X-CBO-ID` request header, resolved server-side against the caller's
 * token. `/api/members` is therefore the same URL for every org on the platform,
 * and a key built from the URL alone made this cache a cross-tenant leak: a
 * super_admin who viewed a demo org, switched back to her own, and then went
 * offline was served the demo org's members under her own org's name.
 *
 * So the identity the response was fetched under is part of the key. The request
 * header wins over localStorage, because the header is what the server actually
 * answered.
 *
 * The UI language belongs to that scope for the same reason (TRUS-446): field
 * labels, section titles and picklist option labels are resolved server-side
 * from `X-WeaveIn-Language`, so one URL now has one body per language too, and
 * a key without it would replay English labels for a Spanish request.
 */
function identityScope(config: InternalAxiosRequestConfig): string {
  let userId = '';
  let cboId = '';

  try {
    const raw = typeof localStorage !== 'undefined' ? localStorage.getItem('user') : null;
    userId = raw ? String(JSON.parse(raw)?.id ?? '') : '';
    cboId = (typeof localStorage !== 'undefined' && localStorage.getItem('active_cbo_id')) || '';
  } catch {
    /* fall through to whatever the request itself carries */
  }

  try {
    const headers = config.headers as
      | { get?: (name: string) => unknown; [key: string]: unknown }
      | undefined;
    const fromHeader =
      (typeof headers?.get === 'function' ? headers.get('X-CBO-ID') : undefined) ??
      headers?.['X-CBO-ID'] ??
      headers?.['x-cbo-id'];
    if (fromHeader !== undefined && fromHeader !== null && String(fromHeader) !== '') {
      cboId = String(fromHeader);
    }
  } catch {
    /* keep the localStorage value */
  }

  let language = '';
  try {
    const headers = config.headers as
      | { get?: (name: string) => unknown; [key: string]: unknown }
      | undefined;
    const fromHeader =
      (typeof headers?.get === 'function' ? headers.get(UI_LANGUAGE_HEADER) : undefined) ??
      headers?.[UI_LANGUAGE_HEADER] ??
      headers?.[UI_LANGUAGE_HEADER.toLowerCase()];
    language =
      fromHeader !== undefined && fromHeader !== null && String(fromHeader) !== ''
        ? String(fromHeader)
        : currentUiLanguage() || '';
  } catch {
    /* an unkeyed language is still better than a wrong one */
  }

  return `u:${userId || 'anon'}|c:${cboId || 'none'}|l:${language || 'en'}`;
}

export function cacheKeyFor(config: InternalAxiosRequestConfig): string {
  const base = config.baseURL || '';
  const url = config.url || '';
  let params = '';
  const p = config.params;
  if (p && typeof p === 'object') {
    params = Object.keys(p)
      .sort()
      .map((k) => `${k}=${JSON.stringify((p as Record<string, unknown>)[k])}`)
      .join('&');
  }
  return `${identityScope(config)}|${base}${url}?${params}`;
}

function isCacheableUrl(config: InternalAxiosRequestConfig): boolean {
  const path = `${config.baseURL || ''}${config.url || ''}`;
  if (NEVER_CACHE.some((p) => path.includes(p))) return false;
  // Don't persist unmasked PII: skip member caching while a reveal session is active.
  try {
    if (
      typeof sessionStorage !== 'undefined' &&
      sessionStorage.getItem('reveal_token') &&
      path.includes('/members')
    ) {
      return false;
    }
  } catch {
    /* ignore */
  }
  return true;
}

function cachedResponse(
  cached: CachedHttp,
  config: InternalAxiosRequestConfig,
): AxiosResponse {
  return {
    data: cached.data,
    status: cached.status || 200,
    statusText: 'OK (offline cache)',
    headers: new axios.AxiosHeaders(),
    config,
    request: {},
  } as AxiosResponse;
}

let installed = false;

export function installOfflineHttpCache(): void {
  if (!isTauri() || installed) return;
  installed = true;

  const baseAdapter: AxiosAdapter = axios.getAdapter(axios.defaults.adapter);

  axios.defaults.adapter = async (config: InternalAxiosRequestConfig) => {
    // Only GETs are cached/served; writes pass straight through (offline writes
    // are queued at the service layer via offlineAwareMutate).
    if (!isGet(config) || !isCacheableUrl(config)) {
      return baseAdapter(config);
    }

    const key = cacheKeyFor(config);

    if (effectiveOffline()) {
      try {
        const cached = await getCachedEntity<CachedHttp>(HTTP_CACHE_TYPE, key);
        if (cached) return cachedResponse(cached, config);
      } catch {
        /* DB not open / not ready — fall through to a fast offline error */
      }
      const err = new axios.AxiosError(
        'Offline: no cached data for this request',
        'ERR_OFFLINE',
        config,
      );
      throw err;
    }

    // Online: fetch, then cache the successful response (best-effort).
    const response = await baseAdapter(config);
    try {
      if (response && response.status >= 200 && response.status < 300) {
        await cacheEntity<CachedHttp>(
          HTTP_CACHE_TYPE,
          key,
          { status: response.status, data: response.data },
          HTTP_CACHE_TTL_SECONDS,
        );
      }
    } catch {
      /* cache write failed (DB not open, quota, etc.) — non-fatal */
    }
    return response;
  };
}

// Self-install on import so simply importing this module first wires it up.
installOfflineHttpCache();
